import React, { useState } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Translate, withLocalize } from 'react-localize-redux';
import Icon, { IconSizes, IconTypes, IconVariants } from '../../components/foundations/Icon';
import VerdictBadge from '../../watchlist/components/VerdictBadge';
import { SENTIMENTS, TRANSLATION_KEY as WATCHLIST_TRANSLATION_KEY } from '../../watchlist/constants';
import { TRANSLATION_KEY } from '../constants';
import { alertShape } from '../propShapes';
import { alertBodyKey, alertBodyText, alertMetrics, isLongSummary } from '../utils';
import AlertMetrics from './AlertMetrics';
import AlertTime from './AlertTime';
import MonitorStillChecking from './MonitorStillChecking';
import css from './MonitorResultToast.css';

const TOAST_KEY = `${TRANSLATION_KEY}.TOASTS`;

const ACCENTS = {
    [SENTIMENTS.HEADS_UP]: 'accentHeadsUp',
    [SENTIMENTS.GOOD_NEWS]: 'accentGoodNews',
};

const MonitorResultToast = ({ alert, checkingCount, translate, onDismiss, onSnooze, onOpen, onOpenThread }) => {
    const [expanded, setExpanded] = useState(false);
    const body = alertBodyText(alert, expanded);
    const bodyKey = body ? null : alertBodyKey(alert);
    const clampable = isLongSummary(alert);
    const metrics = alertMetrics(alert);

    return (
        <div className={css.card}>
            <div className={classNames(css.accent, css[ACCENTS[alert.sentiment] ?? 'accentNeutral'])} />
            <div className={css.content}>
                <div className={css.meta}>
                    <VerdictBadge verdict={alert.verdict} compact />
                    <span className={css.time}>
                        <AlertTime lastRunAt={alert.lastRunAt} finished />
                    </span>
                    <span className={css.spacer} />
                    <button
                        type="button"
                        className={css.closeButton}
                        aria-label={translate(`${TOAST_KEY}.DISMISS`)}
                        onClick={onDismiss}
                    >
                        <Icon
                            type={IconTypes.TABLER}
                            name="x"
                            size={IconSizes.XX_SMALL}
                            variant={IconVariants.LIGHT}
                            interactive={false}
                        />
                    </button>
                </div>

                <div className={css.title}>
                    {alert.title || <Translate id={`${WATCHLIST_TRANSLATION_KEY}.CARD.UNTITLED`} />}
                </div>
                {body && <div className={css.body}>{body}</div>}
                {bodyKey && (
                    <div className={css.body}>
                        <Translate id={`${WATCHLIST_TRANSLATION_KEY}.CARD.${bodyKey}`} />
                    </div>
                )}
                {clampable && (
                    <button
                        type="button"
                        className={css.expandToggle}
                        aria-expanded={expanded}
                        onClick={() => setExpanded(!expanded)}
                    >
                        <Translate id={`${TOAST_KEY}.${expanded ? 'SHOW_LESS' : 'READ_FULL'}`} />
                    </button>
                )}

                {metrics && <AlertMetrics items={metrics} />}

                <div className={css.actions}>
                    <button type="button" className={css.actionButton} onClick={onOpenThread}>
                        <Translate id={`${TOAST_KEY}.OPEN_THREAD`} />
                    </button>
                    <button type="button" className={css.actionButton} onClick={onOpen}>
                        <Translate id={`${TOAST_KEY}.VIEW_IN_WATCHLIST`} />
                    </button>
                    <span className={css.spacer} />
                    <button type="button" className={css.snoozeButton} onClick={onSnooze}>
                        <Translate id={`${TOAST_KEY}.SNOOZE`} />
                    </button>
                </div>
            </div>
            <MonitorStillChecking count={checkingCount} />
        </div>
    );
};

MonitorResultToast.propTypes = {
    alert: alertShape.isRequired,
    checkingCount: PropTypes.number.isRequired,
    translate: PropTypes.func.isRequired,
    onDismiss: PropTypes.func.isRequired,
    onSnooze: PropTypes.func.isRequired,
    onOpen: PropTypes.func.isRequired,
    onOpenThread: PropTypes.func.isRequired,
};

export default withLocalize(MonitorResultToast);
